import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Collapse, LinearProgress, Skeleton } from "@mui/material";

import { Skill } from "../../../types/types";

import { useUserSkills } from "../../User/api/queries/useUserSkills";

import EmptyEndorsedSkillsFigure from "../assets/images/empty-skills-figure.png";

const isSkillsEmpty = (skills: Skill[]) => {
  return skills.every((skill) => {
    const count = parseInt(skill.count || "0", 10);
    const percentage = skill.percentage || 0;
    return count === 0 && percentage === 0;
  });
};

const EndorsedSkills = () => {
  const { userId } = useParams();
  const { data: skills, isLoading: isSkillsLoading, isError: isSkillsError } = useUserSkills(userId!);

  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation("profiles", { keyPrefix: "endorsedSkills" });

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  if (isSkillsLoading) return <EndorsedSkillsLoader />;
  if (skills?.length === 0 || isSkillsEmpty(skills!)) return <EmptyEndorsedSkills />;

  const initialSkills = skills?.slice(0, 4);
  const remainingSkills = skills?.slice(4);

  return (
    <Box
      sx={{
        backgroundColor: "var(--white, #FFF)",
        padding: "20px",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: "#FFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        rowGap: "16px",
      }}
    >
      <Typography
        sx={{
          color: "var(--black-100, #1C1C1C)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "24px",
        }}
      >
        {t("topEndorsedSkills")}
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          rowGap: "12px",
        }}
      >
        {initialSkills?.map((skill, index) => (
          <SkillMeter key={index} skill={skill} />
        ))}
        {remainingSkills?.length && remainingSkills?.length > 0 && (
          <>
            <Collapse in={expanded}>
              {remainingSkills?.map((skill, index) => (
                <SkillMeter key={index + 4} skill={skill} />
              ))}
            </Collapse>
            <Button
              variant="text-sm"
              fullWidth
              onClick={handleExpandClick}
              sx={{ marginTop: "17px", borderRadius: "4px" }}
            >
              {expanded ? t("showLess") : t("showMore")}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EndorsedSkills;

export const SkillMeter = ({ skill: { title, percentage, count } }: { skill: Skill }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Typography variant="body12Bold">{title}</Typography>
        <Typography variant="body10Bold">
          {percentage || 0}% ⋅ {count}
        </Typography>
      </Box>
      <LinearProgress
        sx={{
          height: "16px",
          borderRadius: "4px",
          backgroundColor: "#E0E0E0",
          "& .MuiLinearProgress-bar": {
            background: "linear-gradient(270deg, #039103 0%, rgba(3, 145, 3, 0.60) 100%)",
          },
        }}
        variant="determinate"
        value={percentage || 0}
      />
    </Box>
  );
};

const EndorsedSkillsLoader = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "endorsedSkills" });

  return (
    <Box
      sx={{
        backgroundColor: "var(--white, #FFF)",
        padding: "20px",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: "#FFF",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        rowGap: "16px",
      }}
    >
      <Typography
        sx={{
          color: "var(--black-100, #1C1C1C)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "24px",
        }}
      >
        {t("topEndorsedSkills")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}>
        {[...Array(4)].map((_, index) => (
          <Box key={index} sx={{ display: "flex", flexDirection: "column", rowGap: "6px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Skeleton variant="text" width={120} height={16} />
              <Skeleton variant="text" width={60} height={14} />
            </Box>
            <Skeleton variant="rectangular" height={16} sx={{ borderRadius: "4px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const EmptyEndorsedSkills = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "endorsedSkills" });

  return (
    <Box
      sx={{
        backgroundColor: "var(--white, #FFF)",
        padding: "20px",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: "#FFF",
        display: "flex",
        flexDirection: "column",
        rowGap: "16px",
      }}
    >
      <Typography
        sx={{
          color: "var(--black-100, #1C1C1C)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "24px",
        }}
      >
        {t("topEndorsedSkills")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        <img src={EmptyEndorsedSkillsFigure} alt="empty-endorsed-skills-figure" width={107} height={80} />
        <Typography
          sx={{
            color: "var(--Dark---1000, #111)",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {t("noEndorsedSkills")}
        </Typography>
      </Box>
    </Box>
  );
};
